// redux/slices/librarySlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchLibrariesList,
  fetchLibraryAuthTypes,
  createLibrary,
  updateLibrary,
  LibraryPayload,
  LibraryConfig,
} from "../../apis/libraryApis";

export interface LibraryRow {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  status: "Active" | "Inactive";
  contactEmail?: string;
  originalConfig?: LibraryConfig[];
  authTypeId?: string;
  connectionType?: string;
}

interface AuthTypeOption {
  label: string;
  value: string;
}

interface LibraryState {
  data: LibraryRow[];
  authTypes: AuthTypeOption[];
  loading: boolean;
  error: string | null;
  needRefresh: boolean;
}

const initialState: LibraryState = {
  data: [],
  authTypes: [],
  loading: false,
  error: null,
  needRefresh: true,
};

export const fetchLibraries = createAsyncThunk(
  "library/fetchLibraries",
  async () => {
    const [libraryRes, authTypeRes] = await Promise.all([
      fetchLibrariesList(),
      fetchLibraryAuthTypes(),
    ]);

    const authTypeMap: Record<string, string> = {};
    authTypeRes.data.forEach((authType: any) => {
      authTypeMap[authType.id || authType._id] = authType.libraryAuthName;
    });

    const mappedAuthTypes: AuthTypeOption[] = authTypeRes.data.map(
      (authType: any) => ({
        label: authType.libraryAuthName,
        value: authType.id || authType._id,
      })
    );

    const mappedData: LibraryRow[] = libraryRes.data.map((lib: any) => ({
      id: lib.id || lib._id,
      title: lib.name,
      subtitle: "",
      description: authTypeMap[lib.libraryAuthTypeId] || "Unknown",
      status: lib.isActive ? "Active" : "Inactive",
      contactEmail: lib.contactEmail,
      originalConfig: lib.libraryConfig as LibraryConfig[],
      authTypeId: lib.libraryAuthTypeId,
      connectionType: lib?.libraryConfig[0]?.connectionType,
    }));

    return { libraries: mappedData, authTypes: mappedAuthTypes };
  }
);

export const createLibraryThunk = createAsyncThunk(
  "library/createLibrary",
  async (payload: LibraryPayload, { rejectWithValue }) => {
    try {
      await createLibrary(payload);
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  }
);

export const updateLibraryThunk = createAsyncThunk(
  "library/updateLibrary",
  async (
    { id, payload }: { id: string; payload: LibraryPayload },
    { rejectWithValue }
  ) => {
    try {
      await updateLibrary(id, payload);
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  }
);

const librarySlice = createSlice({
  name: "library",
  initialState,
  reducers: {
    setLibraryRefresh: (state, action: PayloadAction<boolean>) => {
      state.needRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLibraries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLibraries.fulfilled, (state, action) => {
        state.data = action.payload.libraries;
        state.authTypes = action.payload.authTypes;
        state.loading = false;
        state.needRefresh = false;
      })
      .addCase(fetchLibraries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch libraries";
      })

      .addCase(createLibraryThunk.fulfilled, (state) => {
        state.needRefresh = true;
      })
      .addCase(updateLibraryThunk.fulfilled, (state) => {
        state.needRefresh = true;
      });
  },
});

export const { setLibraryRefresh } = librarySlice.actions;
export default librarySlice.reducer;
